import { FeatureCollection, Point } from 'geojson'
import gausiumMapPinAsset from 'images/icons/gausium-map-pin.png'
import { useEffect, useMemo, useState } from 'react'
import { Layer, MapboxMap, Source } from 'react-map-gl'

/** API / OpenAPI often types GeoJSON `type` as string; runtime check below. */
export type GausiumMapGeoPin = {
  id: number
  name: string
  geo_point?: { type?: string; coordinates?: number[] } | null
  /** From `GausiumRobotMap.charging_station_pose` (admin); floor-map pixels + optional angleDeg. */
  charging_station_pose?: unknown
}

const emptyFc: FeatureCollection<Point> = { type: 'FeatureCollection', features: [] }

/** Normalize before addImage (max edge in CSS pixels). */
const GS_MAP_ICON_MAX_PX = 300

function makeNormalizedMapIconImage(img: HTMLImageElement): HTMLCanvasElement | null {
  const w = img.naturalWidth
  const h = img.naturalHeight
  if (!w || !h) return null
  const scale = Math.min(GS_MAP_ICON_MAX_PX / w, GS_MAP_ICON_MAX_PX / h, 1)
  const cw = Math.max(1, Math.round(w * scale))
  const ch = Math.max(1, Math.round(h * scale))
  const canvas = document.createElement('canvas')
  canvas.width = cw
  canvas.height = ch
  const ctx = canvas.getContext('2d')
  if (!ctx) return null
  ctx.drawImage(img, 0, 0, cw, ch)
  return canvas
}

const GAUSIUM_MAP_PIN_IMAGE = 'gausium-map-pin'

/** Re-register icon after basemap/style swap (Mapbox clears images). */
function addGausiumMapPinImage(map: MapboxMap, iconName: string): Promise<void> {
  return new Promise(resolve => {
    if (map.hasImage(iconName)) {
      resolve()
      return
    }
    const img = new Image()
    img.onload = () => {
      if (!map || map.hasImage(iconName)) {
        resolve()
        return
      }
      const normalized = makeNormalizedMapIconImage(img)
      const add = (image: HTMLImageElement | ImageBitmap) => {
        if (map.hasImage(iconName)) return
        try {
          map.addImage(iconName, image)
        } catch (e) {
          console.warn('[GausiumMapGeoPins] map.addImage failed:', e)
        }
      }
      if (normalized) {
        createImageBitmap(normalized)
          .then(bitmap => add(bitmap))
          .catch(() => add(img))
          .finally(() => resolve())
      } else {
        add(img)
        resolve()
      }
    }
    img.onerror = () => {
      console.warn('[GausiumMapGeoPins] failed to load pin image asset')
      resolve()
    }
    img.src = gausiumMapPinAsset
  })
}

const GausiumMapGeoPinsOnMap = ({
  map,
  pins,
  highlightedId,
  onPinClick,
}: {
  map: MapboxMap | undefined
  pins: GausiumMapGeoPin[]
  highlightedId?: number
  onPinClick?: (id: number) => void
}) => {
  const iconName = GAUSIUM_MAP_PIN_IMAGE
  /** Bumped after icon is registered / style swaps so react-map-gl re-adds Source/Layer. */
  const [styleEpoch, setStyleEpoch] = useState(0)
  /** Symbol layer must not mount until `map.addImage` finished — otherwise Mapbox logs styleimagemissing / broken first paint. */
  const [imageReady, setImageReady] = useState(false)

  const data = useMemo((): FeatureCollection<Point> => {
    const features = pins
      .filter(p => p.geo_point?.type === 'Point' && p.geo_point.coordinates?.length === 2)
      .map(p => ({
        type: 'Feature' as const,
        geometry: {
          type: 'Point' as const,
          coordinates: p.geo_point!.coordinates as [number, number],
        },
        properties: {
          id: p.id,
          name: p.name || '',
          highlighted: p.id === highlightedId ? 1 : 0,
        },
      }))
    return features.length ? { type: 'FeatureCollection', features } : emptyFc
  }, [pins, highlightedId])

  useEffect(() => {
    if (!map || !onPinClick) {
      return
    }
    const handler = (e: { features?: Array<{ properties?: { id?: number } | null }> }) => {
      const id = e.features?.[0]?.properties?.id
      if (typeof id === 'number') onPinClick(id)
      else if (id != null) onPinClick(Number(id))
    }
    map.on('click', 'gausium-map-geo-pins', handler as never)
    return () => {
      map.off('click', 'gausium-map-geo-pins', handler as never)
    }
  }, [map, onPinClick, styleEpoch])

  /** If Mapbox tries to draw the symbol before our async addImage completes, load the bitmap here. */
  useEffect(() => {
    if (!map) return
    const onStyleImageMissing = (e: { id?: string }) => {
      if (e.id !== iconName) return
      void addGausiumMapPinImage(map, iconName).then(() => {
        try {
          map.triggerRepaint()
        } catch {
          /* ignore */
        }
        if (map.hasImage(iconName)) {
          setImageReady(true)
          setStyleEpoch((x: number) => x + 1)
        }
      })
    }
    map.on('styleimagemissing', onStyleImageMissing as never)
    return () => {
      map.off('styleimagemissing', onStyleImageMissing as never)
    }
  }, [map, iconName])

  useEffect(() => {
    if (!map) {
      setImageReady(false)
      return
    }
    if (map.hasImage(iconName)) {
      setImageReady(true)
    }
    let cancelled = false
    let idleHandler: (() => void) | undefined
    const detachIdle = () => {
      if (idleHandler) {
        map.off('idle', idleHandler)
        idleHandler = undefined
      }
    }
    const onStyleLoad = () => {
      detachIdle()
      if (!map.hasImage(iconName)) {
        setImageReady(false)
      }
      addGausiumMapPinImage(map, iconName).then(() => {
        if (cancelled) return
        if (map.hasImage(iconName)) {
          setImageReady(true)
        } else {
          setImageReady(false)
        }
        setStyleEpoch((e: number) => e + 1)
        try {
          map.triggerRepaint()
        } catch {
          /* ignore */
        }
      })
    }
    const trySyncWhenReady = () => {
      if (!map.isStyleLoaded()) return false
      onStyleLoad()
      return true
    }
    map.on('style.load', onStyleLoad)
    if (!trySyncWhenReady()) {
      idleHandler = () => {
        if (cancelled) return
        if (trySyncWhenReady()) {
          map.off('idle', idleHandler!)
          idleHandler = undefined
        }
      }
      map.on('idle', idleHandler)
    }
    return () => {
      cancelled = true
      detachIdle()
      setImageReady(false)
      map.off('style.load', onStyleLoad)
    }
  }, [map, iconName])

  /** Without map we cannot addImage; do not mount symbol layer until bitmap is in the style (avoids race on first tab / first paint). */
  if (!map || data.features.length === 0 || !imageReady) return null

  return (
    <Source key={`gausium-map-geo-pins-${styleEpoch}`} id="gausium-map-geo-pins" type="geojson" data={data}>
      <Layer
        id="gausium-map-geo-pins"
        source="gausium-map-geo-pins"
        type="symbol"
        layout={{
          'icon-image': iconName,
          'icon-anchor': 'center',
          'icon-allow-overlap': true,
          // Match AllRobotsOnMap robot markers (0.6 @ z15 → 3.2 @ z22); slight bump when highlighted.
          'icon-size': [
            'interpolate',
            ['exponential', 2],
            ['zoom'],
            15,
            ['case', ['==', ['get', 'highlighted'], 1], 0.7, 0.6],
            22,
            ['case', ['==', ['get', 'highlighted'], 1], 3.5, 3.2],
          ],
        }}
      />
    </Source>
  )
}

export default GausiumMapGeoPinsOnMap
